/**
 * Short-run cost curves from a table of output and cost.
 *
 * Definitions as taught in OpenStax Principles of Economics and Mankiw: with fixed cost
 * FC (cost that does not change with output), variable cost VC = TC − FC, and output Q,
 *
 *   AFC = FC ÷ Q    AVC = VC ÷ Q    ATC = TC ÷ Q = AFC + AVC
 *   MC  = ΔTC ÷ ΔQ  (the extra cost of the extra output between two listed rows)
 *
 * A competitive firm earns zero economic profit at the minimum of ATC (the break-even
 * price) and stops producing in the short run below the minimum of AVC (the shutdown
 * price). Both minima are taken over the outputs listed, so they are exact only to the
 * resolution of the table.
 */

export interface CostPoint {
  q: number;
  /** The cost the user typed: total cost or variable cost, per the chosen mode. */
  cost: number;
}

export interface CostRow {
  q: number;
  tc: number;
  fc: number;
  vc: number;
  /** Null at Q = 0, where a per-unit figure would divide by zero. */
  afc: number | null;
  avc: number | null;
  atc: number | null;
  /** ΔTC ÷ ΔQ from the previous row; null at Q = 0. */
  mc: number | null;
  /** The output the marginal cost is measured from, to place MC between rows. */
  mcFrom: number | null;
}

export type CostError =
  | "no-rows"
  | "duplicate-output"
  | "negative"
  | "need-fixed-cost"
  | "cost-below-fixed"
  | "no-positive-output";

export interface CostTable {
  fixedCost: number;
  rows: CostRow[];
  /** Outputs at which total cost falls as output rises — worth a warning. */
  fallingCost: number[];
}

type Mode = "tc" | "vc";

/** Sorted points and the fixed cost, or the reason the data cannot make a table. */
function validate(
  points: readonly CostPoint[],
  mode: Mode,
  fixedCostInput: number | null
): { sorted: CostPoint[]; fc: number } | CostError {
  if (points.length === 0) {
    return "no-rows";
  }
  const sorted = [...points].sort((a, b) => a.q - b.q);
  if (sorted.some((p) => p.q < 0 || p.cost < 0)) {
    return "negative";
  }
  if (sorted.some((p, i) => i > 0 && p.q === sorted[i - 1].q)) {
    return "duplicate-output";
  }
  const zeroRow = sorted.find((p) => p.q === 0);
  const fc = mode === "tc" && zeroRow ? zeroRow.cost : fixedCostInput;
  if (fc === null || fc < 0) {
    return "need-fixed-cost";
  }
  if (!sorted.some((p) => p.q > 0)) {
    return "no-positive-output";
  }
  return { sorted, fc };
}

/** The cost table, or the reason the data cannot make one. */
export function buildCostTable(
  points: readonly CostPoint[],
  mode: Mode,
  fixedCostInput: number | null
): CostTable | CostError {
  const checked = validate(points, mode, fixedCostInput);
  if (typeof checked === "string") {
    return checked;
  }
  const { sorted, fc } = checked;
  const totalOf = (point: CostPoint) =>
    mode === "tc" ? point.cost : fc + point.cost;
  if (sorted.some((p) => p.q > 0 && totalOf(p) < fc)) {
    return "cost-below-fixed";
  }
  // Output zero costs the fixed cost whether or not the user listed that row.
  const rows: CostRow[] = sorted[0].q === 0 ? [] : [zeroOutput(fc, fc)];
  const fallingCost: number[] = [];
  let previous = { q: 0, tc: fc };
  for (const point of sorted) {
    const tc = totalOf(point);
    if (point.q === 0) {
      rows.push(zeroOutput(fc, tc));
    } else {
      rows.push({
        q: point.q,
        tc,
        fc,
        vc: tc - fc,
        afc: fc / point.q,
        avc: (tc - fc) / point.q,
        atc: tc / point.q,
        mc: (tc - previous.tc) / (point.q - previous.q),
        mcFrom: previous.q,
      });
      if (tc < previous.tc) {
        fallingCost.push(point.q);
      }
    }
    previous = { q: point.q, tc };
  }
  return { fixedCost: fc, rows, fallingCost };
}

function zeroOutput(fc: number, tc: number): CostRow {
  return {
    q: 0,
    tc,
    fc,
    vc: tc - fc,
    afc: null,
    avc: null,
    atc: null,
    mc: null,
    mcFrom: null,
  };
}

/** The row with the smallest value of a per-unit cost (lowest output on a tie). */
export function minimumBy(
  rows: readonly CostRow[],
  pick: (row: CostRow) => number | null
): CostRow | null {
  let best: CostRow | null = null;
  let bestValue = Number.POSITIVE_INFINITY;
  for (const row of rows) {
    const value = pick(row);
    if (value !== null && value < bestValue - 1e-12) {
      best = row;
      bestValue = value;
    }
  }
  return best;
}

export type ShortRunDecision = "profit" | "breakEven" | "loss" | "shutDown";

export interface PriceOutcome {
  /** The profit-maximising listed output; 0 means shut down. */
  q: number;
  revenue: number;
  profit: number;
  decision: ShortRunDecision;
  /** Profit at every row, in row order (zero output included). */
  profits: number[];
}

/**
 * The best listed output at a market price: the one with the highest profit
 * P·Q − TC, the lowest output on a tie. Zero output is always available and loses
 * exactly the fixed cost.
 */
export function atPrice(table: CostTable, price: number): PriceOutcome {
  const profits = table.rows.map((row) => price * row.q - row.tc);
  let bestIndex = 0;
  for (const [index, profit] of profits.entries()) {
    if (profit > profits[bestIndex] + 1e-9) {
      bestIndex = index;
    }
  }
  const best = table.rows[bestIndex];
  const profit = profits[bestIndex];
  let decision: ShortRunDecision = "loss";
  if (best.q === 0) {
    decision = "shutDown";
  } else if (Math.abs(profit) < 1e-9) {
    decision = "breakEven";
  } else if (profit > 0) {
    decision = "profit";
  }
  return { q: best.q, revenue: price * best.q, profit, decision, profits };
}
